import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useBreakpoint, Breakpoint } from "../hooks/useBreakpoint";

export type SidebarMode = "drawer" | "mini" | "full";

interface LayoutContextValue {
  breakpoint: Breakpoint;
  sidebarMode: SidebarMode;
  isSidebarOpen: boolean;
  sidebarCollapsed: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  closeSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleCollapse: () => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export const useLayout = (): LayoutContextValue => {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return ctx;
};

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const breakpoint = useBreakpoint();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleCollapse = useCallback(() => setSidebarCollapsed((v) => !v), []);

  // Auto-collapse/expand based on breakpoint
  useEffect(() => {
    if (breakpoint === "mobile" || breakpoint === "tablet") {
      setSidebarOpen(false);
    }
    if (breakpoint === "laptop") {
      setSidebarCollapsed(true);
    }
    if (breakpoint === "desktop") {
      setSidebarCollapsed(false);
    }
  }, [breakpoint]);

  // Derive sidebar mode from breakpoint + collapsed state
  let sidebarMode: SidebarMode;
  if (breakpoint === "mobile" || breakpoint === "tablet") {
    sidebarMode = "drawer";
  } else if (sidebarCollapsed) {
    sidebarMode = "mini";
  } else {
    sidebarMode = "full";
  }

  return (
    <LayoutContext.Provider
      value={{
        breakpoint,
        sidebarMode,
        isSidebarOpen,
        sidebarCollapsed,
        setSidebarOpen,
        toggleSidebar,
        closeSidebar,
        setSidebarCollapsed,
        toggleCollapse,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
